<div id="app">
  <div class="signing-container py-4">
    <!-- Header -->
    <div class="mb-3">
      <P class="mb-0 fw-bold text-x">Signing Flow</P>
      <div class="text-secondary text-xxs mt-2">
        Sequential approval process
        <span class="signing-dots">
          <span class="active"></span>
          <span></span>
          <span></span>
        </span>
      </div>
    </div>

    <!-- TEMPLATE SELECTOR -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0 text-xs text-dark">
          Assigned Templates
        </h6>
        <span class="text-xxs text-muted">
          Select a template below to view &amp; configure its signing flow
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-if="assigned_templates.length === 0"
        class="text-xs text-muted py-3"
      >
        No templates assigned to this entity type yet.
      </div>

      <!-- Full-width stacked cards -->
      <div v-else class="d-flex flex-column gap-2">
        <div
          v-for="template in assigned_templates"
          :key="template.id"
          class="w-100"
        >
          <!-- CARD -->
          <div
            class="card shadow-sm border-0 w-100 template-card"
            :class="{
              'template-card-selected':
                selected_template_id === template.template_id
            }"
            role="button"
            @click="handle_template_click(template)"
          >
            <div class="card-body p-3">
              <!-- First row: icon + title + status groups + overall status -->
              <div
                class="d-flex align-items-start justify-content-between gap-3"
              >
                <!-- LEFT: Icon + template label + per-step status groups -->
                <div class="d-flex align-items-start gap-3 flex-grow-1">
                  <div
                    class="template-icon rounded-circle d-flex align-items-center justify-content-center"
                  >
                    <i class="bi bi-file-earmark-text"></i>
                  </div>

                  <div class="flex-grow-1">
                    <div class="text-xxs text-muted text-uppercase mb-1">
                      Template
                    </div>
                    <div class="fw-500">
                      {{ template.template_name }}
                    </div>

                    <!-- STEP STATUS GROUPS (only for selected template) -->
                    <div
                      v-if="
                        selected_template_id === template.template_id &&
                        local_steps.length > 0
                      "
                      class="d-flex align-items-center gap-3 mt-3 flex-wrap template-status-groups"
                    >
                      <div
                        v-for="step in local_steps"
                        :key="'summary-' + step.id"
                        class="template-status-group text-center"
                      >
                        <div
                          class="template-status-icon"
                          :class="'status-' + get_step_overall_status(step)"
                        >
                          <i
                            :class="get_step_status_icon_class(
                              get_step_overall_status(step)
                            )"
                          ></i>
                        </div>
                        <div
                          class="template-status-label text-xxs fw-500 mt-1 text-truncate"
                        >
                          {{ step.title }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- RIGHT: overall submission status -->
                <div class="text-end">

                  <!-- Row with status + button -->
                  <div class="d-flex align-items-center justify-content-end gap-2 mb-1">

                    <span
                      class="text-xs fw-bold"
                      :class="get_template_status_class(template)"
                    >
                      Status: {{ get_template_status_label(template) }}
                    </span>

                    <button
                      v-if="get_template_status_label(template).toLowerCase() === 'completed' && role !== 'Entity'"
                      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                      @click.stop="download_signed_document(template.submission_id)"
                    >
                      Download PDF
                    </button>

                  </div>

                  <div class="text-xxs text-muted">
                    Document submission status
                  </div>

                </div>

              </div>

              <!-- Second row: actions -->
              <div
                class="d-flex justify-content-between align-items-center mt-3 pt-2 border-top"
              >
                <div class="d-flex align-items-center gap-2 text-xxs text-muted">
                  <i class="bi bi-diagram-3"></i>
                  <span class="text-dark">
                    Signing flow timeline will show below once selected
                  </span>
                </div>

                <div class="d-flex align-items-center gap-2">

                  <button
                    v-if="role !== 'Entity'"
                    type="button"
                    class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                    @click.stop="handle_template_click(template)"
                  >
                    View Signing Flow
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                    @click.stop="preview_template(template)"
                    data-bs-toggle="modal"
                    data-bs-target="#preview_template_modal"
                  >
                    <span v-if="!is_preview_loading">Preview</span>
                    <span v-else>Loading…</span>
                  </button>

                </div>
              </div>
            </div>
          </div>
          <!-- /CARD -->

          <!-- TIMELINE UNDER THIS CARD ONLY -->
          <div
            class="signing-timeline mt-3"
            v-if="
              selected_template_id === template.template_id &&
              local_steps.length > 0
            "
          >
            <div
              v-for="(step, step_index) in local_steps"
              :key="step.id"
              class="mb-3"
            >
              <!-- node + arrow (top is measured) -->
              <div
                class="signing-timeline-node"
                :style="{ top: (node_top_offsets[step_index] ?? 0) + 'px' }"
              >
                <span class="signing-node-index">{{ step_index + 1 }}</span>
              </div>
              <div
                class="signing-timeline-arrow"
                :style="{ top: (node_top_offsets[step_index] ?? 0) + 'px' }"
                v-if="step_index < local_steps.length - 1"
              ></div>

              <!-- Step card -->
              <div
                class="signing-step-card"
                :class="{
                  active: step.state === 'current' || step.expanded
                }"
                :ref="(element) => (step_element_refs[step_index] = element)"
                @click="toggle_expand(step)"
              >
                <div class="d-flex align-items-start">
                  <div class="flex-grow-1">
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <div class="signing-step-badge text-uppercase">
                        Step {{ step_index + 1 }}
                      </div>

                      <!-- RIGHT SIDE: Manage + chevron -->
                      <div class="d-flex align-items-center gap-2">
                        <button
                          v-if="role !== 'Entity'"
                          type="button"
                          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                          data-bs-toggle="modal"
                          data-bs-target="#assign_users_to_workflow"
                          @click.stop="open_manage(step)"
                        >
                          Manage
                        </button>

                        <i class="bi bi-chevron-right signing-chev"></i>
                      </div>
                    </div>

                    <div class="mt-1 fw-500">{{ step.title }}</div>

                    <!-- Signer chips -->
                    <div
                      class="mt-2 d-flex align-items-center gap-3 flex-wrap"
                    >
                      <span class="signing-signers-count">
                        {{ step.signers.length }} signers assigned
                      </span>

                      <div class="d-flex gap-2 flex-wrap">
                        <span
                          class="signing-signer-chip"
                          v-for="(signer_name, signer_index) in step.signers"
                          :key="signer_index"
                        >
                          <span class="signing-chip-dot"></span>
                          {{ signer_name }}
                        </span>
                      </div>
                    </div>

                    <!-- Owner line when not expanded -->
                    <div
                      class="text-secondary text-xs mt-2"
                      v-if="step.owner && !step.expanded"
                    >
                      {{ step.owner }}
                    </div>

                    <!-- Expanded details -->
                    <div
                      v-if="
                        step.expanded &&
                        step.signers_meta &&
                        step.signers_meta.length > 0
                      "
                      class="mt-3 pt-3 border-top"
                    >
                      <div class="text-xs text-secondary mb-2">
                        Assigned signers details
                      </div>

                      <div
                        v-for="(signer, signer_index) in step.signers_meta"
                        :key="'meta-' + signer_index"
                        class="signing-signer-row d-flex align-items-center justify-content-between mb-2"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <i
                            :class="get_submitter_icon_class(
                              signer.status_label
                            )"
                          ></i>
                          <div>
                            <div
                              class="d-flex align-items-center gap-1 mb-1"
                            >
                              <i
                                class="bi bi-person-circle text-muted small"
                              ></i>
                              <span class="text-sm fw-500">{{
                                signer.name
                              }}</span>
                            </div>
                            <div class="text-xxs text-muted">
                              {{ signer.email }}
                            </div>
                          </div>
                        </div>

                        <div class="d-flex align-items-center gap-2">
                          <span
                            class="status-badge rounded-pill text-xxs signing-status-badge"
                            :class="get_submitter_badge_class(signer.status_label)"
                          >
                            {{ signer.docuseal_status_label }}
                          </span>

                          <!-- RESEND BUTTON (only for non-completed signers) -->
                          <button

                            v-if="role !== 'Entity' && ['sent','opened','awaiting','pending'].includes(normalise_status(signer.docuseal_status_label))"
                            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                            @click.stop="resend_submitter_email(signer)"
                          >
                            Resend Email
                          </button>

                        </div>

                      </div>
                    </div>
                    <!-- /Expanded details -->
                  </div>
                </div>
              </div>
              <!-- /Step card -->
            </div>
            <!-- /v-for step -->
          </div>
          <!-- /TIMELINE -->
        </div>
        <!-- /v-for wrapper -->
      </div>
    </div>
    <!-- /TEMPLATE SELECTOR -->



    <!-- /TEMPLATE SELECTOR -->


    <!-- MANAGE MODAL -->
  <teleport to="body">
    <div
      class="modal fade"
      id="assign_users_to_workflow"
      tabindex="-1"
      aria-labelledby="assign_users_to_workflow_modal_label"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow-lg">
          <!-- Header -->
          <div
            class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2"
          >
            <h5
              class="modal-title text-white text-xxs m-0"
              id="assign_users_to_workflow_modal_label"
            >
              Assign User To Workflow
            </h5>
            <button
              type="button"
              class="btn-close modal-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <!-- Body -->
          <div class="modal-body p-3">
            <!-- Only render table when we have real rows -->
            <div
              class="table-responsive"
              v-if="assignable_user_rows.length > 0"
            >
              <table
                class="table table-hover"
                id="assign_users_table"
                ref="assignUsersTable"
                :key="assign_users_table_key"
              >
                <thead class="custom-header">
                  <tr>
                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                      No
                    </th>
                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                      First Name
                    </th>
                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                      Last Name
                    </th>
                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                      Email
                    </th>
                    <th class="text-uppercase text-xs font-weight-bolder opacity-7 text-start ps-3">
                      Assign
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(user, index) in assignable_user_rows"
                    :key="user.id"
                    class="text-xxs"
                  >
                    <td class="text-xs text-start">{{ index + 1 }}</td>
                    <td class="text-xs text-start">
                      {{ user.first_name || '' }}
                    </td>
                    <td class="text-xs text-start">
                      {{ user.last_name || '' }}
                    </td>
                    <td class="text-xs text-start">
                      {{ user.email || '—' }}
                    </td>
                    <td class="text-xs text-start">
                      <div
                        class="d-flex justify-content-center align-items-center gap-2"
                      >
                        <div class="form-check form-switch m-0">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            role="switch"
                            :id="'assign-toggle-' + user.id"
                            v-model="user.is_assigned"
                            :disabled="user.is_loading"
                          />
                        </div>
                        <span
                          class="badge"
                          :class="user.is_assigned ? 'bg-success' : 'bg-secondary'"
                        >
                          {{ user.is_assigned ? 'Assigned' : 'Unassigned' }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="modal-footer border-0 p-2 d-flex justify-content-end"
          >
            <button
              type="button"
              class="btn btn-sm btn-primary-dark text-xxs"
              @click="save_assignments"
            >
              Save Assignments
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
    <!-- PREVIEW MODAL -->
    <teleport to="body">
      <div
        class="modal fade"
        id="preview_template_modal"
        tabindex="-1"
        aria-labelledby="preview_template_modal_label"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered">
          <div class="modal-content border-0 shadow-lg">
            <!-- Header -->
            <div class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2">
              <h5 class="modal-title text-white text-xs m-0">
                Document Preview
              </h5>
              <button
                type="button"
                class="btn-close modal-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-0">
              <!-- Loading State -->
              <div v-if="is_preview_loading" class="text-center py-5">
                <div class="spinner-border spinner-border-sm text-primary mb-2" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mb-0 text-xs">Generating document preview...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="!preview_url" class="text-center py-5 text-xs text-muted">
                <p class="mb-0">No preview available</p>
              </div>

              <!-- DocuSeal Form Container -->
              <docuseal-form
                v-else
                :data-src="preview_url"
                data-preview="true">
              </docuseal-form>
            </div>

            <!-- Footer -->
            <div class="modal-footer border-0 p-2 d-flex justify-content-end">
              <button
                v-if="role !== 'Entity'"
                type="button"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                @click.stop="confirm_send_contract(selected_template)"
              >
                Send Contract
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>
    <!-- /PREVIEW MODAL -->
  </div>
</div>
